import React, { createContext, useState, useEffect, useContext } from 'react';
import { apiRequest, clearCsrfToken } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is logged in on initial load
  useEffect(() => {
    const checkLoginStatus = async () => {
      try {
        const userData = await apiRequest('/api/auth/current_user', 'GET');
        if (userData) {
          setCurrentUser(userData);
        } else {
          setCurrentUser(null);
        }
      } catch (err) {
        setCurrentUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkLoginStatus();
  }, []);

  // ====================
  // MULTI-TIER LOGIN FUNCTIONS
  // ====================

  // Super Admin Login
  const loginSuperAdmin = async (email, password) => {
    setError(null);
    try {
      const userData = await apiRequest('/api/auth/super-admin/login', 'POST', {
        email,
        password
      });
      setCurrentUser(userData);
      clearCsrfToken();
      return userData;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  };

  // School Admin Login
  const loginSchoolAdmin = async (email, password) => {
    setError(null);
    try {
      const userData = await apiRequest('/api/auth/school-admin/login', 'POST', {
        email,
        password
      });
      setCurrentUser(userData);
      clearCsrfToken();
      return userData;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  };

  // Teacher Login
  const loginTeacher = async (email, password) => {
    setError(null);
    try {
      const userData = await apiRequest('/api/auth/teacher/login', 'POST', {
        email,
        password
      });
      setCurrentUser(userData);
      clearCsrfToken();
      return userData;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  };

  // Student Login
  const loginStudent = async (username, password) => {
    setError(null);
    try {
      const userData = await apiRequest('/api/auth/student/login', 'POST', {
        username,
        password
      });
      setCurrentUser(userData);
      clearCsrfToken();
      return userData;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  };

  // ====================
  // MULTI-TIER REGISTRATION FUNCTIONS
  // ====================

  // School Admin Registration (with master activation code)
  const registerSchoolAdmin = async (email, password, fullName, masterCode) => {
    setError(null);
    try {
      const response = await apiRequest('/api/auth/school-admin/register', 'POST', {
        activation_code: masterCode,
        email,
        password,
        full_name: fullName
      });
      return response;
    } catch (err) {
      setError(err.message || 'Failed to register');
      throw err;
    }
  };

  // Teacher Registration (with teacher activation code)
  const registerTeacher = async (email, password, fullName, subject, teacherCode) => {
    setError(null);
    try {
      const response = await apiRequest('/api/auth/teacher/register', 'POST', {
        activation_code: teacherCode,
        email,
        password,
        full_name: fullName
      });
      return response;
    } catch (err) {
      setError(err.message || 'Failed to register');
      throw err;
    }
  };

  // Student Registration (COPPA compliant - no email, with student activation code)
  const registerStudent = async (username, password, firstName, lastName, studentCode) => {
    setError(null);
    try {
      const response = await apiRequest('/api/auth/student/register', 'POST', {
        activation_code: studentCode,
        username,
        password,
        first_name: firstName,
        last_name: lastName
      });
      return response;
    } catch (err) {
      setError(err.message || 'Failed to register');
      throw err;
    }
  };

  // ====================
  // LEGACY FUNCTIONS (for backward compatibility)
  // ====================

  // Legacy Login function (redirects to student login)
  const login = async (emailOrUsername, password) => {
    return loginStudent(emailOrUsername, password);
  };

  // Legacy Signup function (not used in multi-tier system)
  const signup = async () => {
    setError('Please use the role-specific registration page');
    throw new Error('Please use the role-specific registration page');
  };

  // Universal Logout function
  const logout = async () => {
    setError(null);
    try {
      await apiRequest('/api/auth/logout', 'POST');
      setCurrentUser(null);
      clearCsrfToken();
    } catch (err) {
      setError(err.message || 'Failed to logout');
      throw err;
    }
  };

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    userType: currentUser?.user_type || null,
    loading,
    error,
    // Multi-tier login functions
    loginSuperAdmin,
    loginSchoolAdmin,
    loginTeacher,
    loginStudent,
    // Multi-tier registration functions
    registerSchoolAdmin,
    registerTeacher,
    registerStudent,
    // Legacy functions
    login,
    signup,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext; 